import { useMsal } from '@azure/msal-react';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Flex,
  Text,
  FormLabel,
  FormControl,
  LinkBox,
  Switch,
  Spacer,
  VStack,
  ScaleFade,
  LinkOverlay,
} from '@chakra-ui/react';
import ClassItemLoading from 'components/class/ClassItemLoading';

import PagingControl from 'components/PagingControl';
import useToastNotif from 'hooks/useToastNotif';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as ReactRouterLink } from 'react-router-dom';
import { getClassesAction } from 'store/modules/class/class-action';
import { ReducerStatus } from 'utils/constants';
import { ADMIN, TRAINER } from 'utils/constants/roles';
import { TOAST_ERROR } from 'utils/constants/toast';
import ClassItem from 'components/class/ClassItem';
import { useInViewport } from 'react-in-viewport';

function ClassListPage() {
  document.title = 'Skill Quest | Class';

  const { accounts } = useMsal();
  const dispatch = useDispatch();

  const ref = useRef(null);

  const { generateToastNotif } = useToastNotif();
  const { inViewport } = useInViewport(
    ref,
    { rootMargin: '100%' },
    { disconnectOnLeave: false },
    {}
  );

  const classes = useSelector((state) => state.class.classes);
  const classesMetaData = useSelector((state) => state.class.classesMetaData);
  const getClasses = useSelector((state) => state.class.getClasses);

  const [currentPage, setCurrentPage] = useState(1);

  // Safe access to user roles with proper null/undefined checks
  const userAccount = accounts?.[0];
  const idTokenClaims = userAccount?.idTokenClaims;
  const roles = idTokenClaims?.roles || [];
  const classPageSize = 12;

  useEffect(() => {
    dispatch(
      getClassesAction({
        currentPage,
        pageSize: classPageSize,
        isAdminMode: false,
      })
    );
  }, [currentPage, dispatch]);

  useEffect(() => {
    if (getClasses.status === ReducerStatus.FAILED) {
      generateToastNotif(
        TOAST_ERROR,
        getClasses.error?.data?.status || '500',
        getClasses.error?.data?.title ||
          'Something went wrong with the request',
        true,
        false
      );
    }
  }, [getClasses, generateToastNotif]);

  return (
    <Box ml={{ base: 0, md: 20 }} minH="100vh" p="10">
      <Flex my={4} gap={2} align="center">
        <Text flex={1} fontSize="xl" fontWeight="bold">
          Classes
        </Text>

        {roles.find((role) => role === TRAINER || role === ADMIN) && (
          <FormControl
            my={2}
            display="flex"
            alignItems="center"
            justifyContent="end"
          >
            <FormLabel htmlFor="edit-mode-off" fontSize="sm" mb="0">
              Edit Mode
            </FormLabel>
            <LinkBox as={ReactRouterLink} to="/admin/classes">
              <Switch id="edit-mode-off" />
            </LinkBox>
          </FormControl>
        )}
      </Flex>

      <VStack minHeight="80vh" align="stretch">
        <VStack align="stretch">
          {getClasses.status === ReducerStatus.LOADING ? (
            <VStack align="stretch" gap={6}>
              {[...Array(5)].map(() => (
                <ClassItemLoading key={uuidv4()} w="100%" minH={350} />
              ))}
            </VStack>
          ) : (
            <ScaleFade
              initialScale={0.9}
              in={getClasses.status !== ReducerStatus.LOADING}
            >
              <VStack align="stretch" gap={6}>
                {classes.map((classItem) => (
                  <Box w="100%" key={classItem.id}>
                    <ScaleFade
                      initialScale={0.9}
                      w="100%"
                      in={inViewport}
                      whileHover={{ scale: 1.01 }}
                    >
                      <LinkBox
                        ref={ref}
                        borderWidth="1px"
                        w="100%"
                        borderRadius="sm"
                        overflow="hidden"
                        h="100%"
                        position="relative"
                      >
                        <LinkOverlay
                          as={ReactRouterLink}
                          to={`/classes/${classItem.id}`}
                        >
                          <ClassItem
                            w="100%"
                            classObject={classItem}
                            isAdminMode={false}
                          />
                        </LinkOverlay>
                      </LinkBox>
                    </ScaleFade>
                  </Box>
                ))}
              </VStack>
            </ScaleFade>
          )}
        </VStack>
        <Spacer mt={12} />

        <PagingControl
          currentPage={currentPage}
          onGoToPage={(newPageNumber) => setCurrentPage(newPageNumber)}
          pageSize={classPageSize}
          totalCount={classesMetaData?.totalItems || 0}
          hasNext={classesMetaData?.hasNext || false}
          totalCountDescription="classes available"
        />
      </VStack>
    </Box>
  );
}

export default ClassListPage;
